/**
 * Prazo automático da solicitação pela prioridade, em dias úteis (#6349).
 *
 * Regra (Victor, 20/10/2026):
 *   - o dia da abertura nunca conta; o prazo começa no dia útil seguinte;
 *   - aberta depois das 16h, ou em fim de semana/feriado, o primeiro dia útil
 *     seguinte também não conta (é como se tivesse sido aberta nele);
 *   - urgência: 1 dia útil · alta: 2 · média: 5 · baixa: sem prazo.
 *   Ex.: média aberta na segunda às 10h vence na segunda seguinte.
 *
 * Quem grava é o banco (trigger em `tarefas`, migration 20261020150000) — esta
 * cópia só mostra o prazo na tela de abrir antes de salvar. As duas precisam
 * concordar; feriado cadastrado na tela de feriados só o banco conhece.
 *
 * Função pura, sem I/O: roda sob `node --test` com type-stripping.
 */

import type { TarefaPrioridade } from '../../types.ts';
import { ehDiaUtil } from './reguaPrazosAgrupamento.ts';

export const DIAS_UTEIS_POR_PRIORIDADE: Record<TarefaPrioridade, number | null> = {
  urgencia: 1,
  alta: 2,
  media: 5,
  baixa: null,
};

/** Hora (de Brasília) a partir da qual o dia da abertura não conta. */
export const HORA_CORTE = 16;

const somaDia = (iso: string, n: number) => {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
};

/** Próximo dia útil depois de `iso` (não conta o próprio dia). */
export function proximoDiaUtil(iso: string): string {
  let d = somaDia(iso, 1);
  while (!ehDiaUtil(d)) d = somaDia(d, 1);
  return d;
}

/** Data e hora de Brasília de um instante. */
function emBrasilia(agora: Date): { dia: string; hora: number } {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(agora);
  const p = (t: string) => partes.find(x => x.type === t)?.value ?? '';
  return { dia: `${p('year')}-${p('month')}-${p('day')}`, hora: Number(p('hour')) };
}

/**
 * Prazo (YYYY-MM-DD) de uma solicitação aberta em `abertaEm` com a prioridade
 * dada. `null` para baixa.
 */
export function prazoDaSolicitacao(prioridade: TarefaPrioridade, abertaEm: Date): string | null {
  const n = DIAS_UTEIS_POR_PRIORIDADE[prioridade];
  if (n == null) return null;
  const { dia, hora } = emBrasilia(abertaEm);
  let base = ehDiaUtil(dia) && hora < HORA_CORTE ? dia : proximoDiaUtil(dia);
  for (let i = 0; i < n; i++) base = proximoDiaUtil(base);
  return base;
}
